import { useEffect, useRef, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import useProgressDashboard from '../hooks/useProgressDashboard';
import AccountSettings from './AccountSettings';
import BillingPanel from './BillingPanel';
import ReferralPanel from './ReferralPanel';
import './Profile.css';

// Niveaux ordonnés par nombre d'évaluations ; les autres badges restent des distinctions indépendantes.
const BADGE_LEVELS = [
  { count: 1, label: 'Première Évaluation', icon: '🎯' },
  { count: 5, label: 'Persévérant', icon: '🔥' },
  { count: 10, label: 'Dévoué', icon: '📚' },
  { count: 20, label: 'Champion', icon: '🏆' },
];

const Profile = ({ focusSection = 'overview', onBackToContent = null }) => {
  const {
    currentUser,
    accessProfile,
    updateProfile,
    resetPassword,
  } = useUser();
  const progressAccessKey = `${accessProfile?.tier || 'free'}:${
    accessProfile?.premiumExpiresAt || 'no-expiry'
  }`;
  const { dashboard } = useProgressDashboard(
    currentUser?.id,
    100,
    progressAccessKey
  );
  const subscriptionRef = useRef(null);
  const [activeSection, setActiveSection] = useState(
    focusSection === 'subscription' ? 'subscription' : 'account'
  );
  const [showProgressHistory, setShowProgressHistory] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState({
    lastName: '',
    firstName: '',
    pseudonym: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [currentPasswordForEmail, setCurrentPasswordForEmail] = useState('');

  const jumpToSubscription = () => {
    setActiveSection('subscription');
    if (activeSection === 'subscription') {
      subscriptionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      subscriptionRef.current?.focus?.({ preventScroll: true });
    }
  };

  useEffect(() => {
    setActiveSection(focusSection === 'subscription' ? 'subscription' : 'account');
  }, [focusSection, currentUser?.id]);

  useEffect(() => {
    if (activeSection !== 'subscription') return;
    subscriptionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    subscriptionRef.current?.focus?.({ preventScroll: true });
  }, [activeSection]);

  // État pour les sections dépliables
  const [expandedSections, setExpandedSections] = useState({
    personalInfo: false,
    security: false,
    preferences: false
  });

  // États pour les données dynamiques des quizzes et simulations
  const [quizStats, setQuizStats] = useState({
    totalQuizzes: 0,
    totalScore: 0,
    averageScore: 0,
    badges: []
  });

  // État pour l'historique groupé par matière (quizzes + simulations)
  const [historyBySubject, setHistoryBySubject] = useState({});
  // État pour les matières étendues
  const [expandedSubjects, setExpandedSubjects] = useState({});

  // Charger les stats depuis le dashboard serveur prioritaire.
  useEffect(() => {
    const combined = Array.isArray(dashboard?.activities)
      ? [...dashboard.activities]
      : [];

    combined.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    const totalItems = combined.length;
    const totalScore = combined.reduce(
      (sum, entry) => sum + (Number(entry.score) || 0),
      0
    );
    const averageScore =
      totalItems > 0
        ? Math.round((totalScore / totalItems) * 10) / 10
        : 0;

    const badges = calculateBadges(combined, totalItems);
    const grouped = groupHistoryBySubject(combined);

    setQuizStats({
      totalQuizzes: totalItems,
      totalScore,
      averageScore,
      badges
    });

    setHistoryBySubject(grouped);
  }, [dashboard]);

  // Grouper l'historique par matière
  const groupHistoryBySubject = (history) => {
    const grouped = {};

    history.forEach(entry => {
      // Les simulations utilisent 'matiere: Simulation'
      const subject = entry.matiere || 'Autre';
      if (!grouped[subject]) {
        grouped[subject] = [];
      }
      grouped[subject].push(entry);
    });

    // Trier chaque groupe par date (plus récent en premier)
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => new Date(b.date) - new Date(a.date));
    });

    return grouped;
  };

  // Calculer les badges basés sur les performances (quizzes + simulations)
  const calculateBadges = (history, totalItems) => {
    const badges = [];

    // Badge : Première évaluation
    if (totalItems >= 1) {
      badges.push({ name: '🎯 Première Évaluation', desc: 'A complété sa première évaluation', color: 'blue' });
    }

    // Badge : 5 évaluations
    if (totalItems >= 5) {
      badges.push({ name: '🔥 Persévérant', desc: 'A complété 5 évaluations', color: 'blue' });
    }

    // Badge : 10 évaluations
    if (totalItems >= 10) {
      badges.push({ name: '📚 Dévoué', desc: 'A complété 10 évaluations', color: 'purple' });
    }

    // Badge : Score parfait
    const perfectScore = history.some(entry => entry.score >= 18);
    if (perfectScore) {
      badges.push({ name: '⭐ Excellence', desc: 'A obtenu 18/20 ou plus', color: 'yellow' });
    }

    // Badge : 20 évaluations
    if (totalItems >= 20) {
      badges.push({ name: '🏆 Champion', desc: 'A complété 20 évaluations', color: 'yellow' });
    }

    // Badge : Moyenne haute
    const avg = totalItems > 0 ? history.reduce((sum, e) => sum + e.score, 0) / totalItems : 0;
    if (avg >= 14) {
      badges.push({ name: '🎓 Expert', desc: 'Moyenne de 14/20 ou plus', color: 'green' });
    }

    // Badge : Mix des matières
    const subjects = new Set(history.map(e => e.matiere));
    if (subjects.size >= 3) {
      badges.push({ name: '🌍 Polyvalent', desc: 'A testé 3 domaines différents', color: 'green' });
    }

    // Badge : Simulation - pour ceux qui font des simulations
    const hasSimulations = history.some(entry => entry.matiere === 'Simulation');
    if (hasSimulations) {
      badges.push({ name: '📝 Simuleur', desc: 'A complété au moins une simulation', color: 'purple' });
    }

    // Badge : Sujets officiels Burkina Faso
    const hasOfficial = history.some(entry => entry.est_sujets_officiels);
    if (hasOfficial) {
      badges.push({ name: '🏆 Sujets Officiels', desc: 'A complété un sujet officiel BF', color: 'yellow' });
    }

    return badges;
  };

  // Calculer la progression globale
  const calculateProgress = () => {
    const { totalQuizzes, averageScore } = quizStats;
    const quizProgress = Math.min(totalQuizzes * 5, 40);
    const scoreProgress = Math.min(averageScore * 2, 60);
    return quizProgress + scoreProgress;
  };

  // Calculer les stats d'une matière
  const getSubjectStats = (subjectEntries) => {
    const count = subjectEntries.length;
    const avg = Math.round((subjectEntries.reduce((sum, e) => sum + e.score, 0) / count) * 10) / 10;
    const best = Math.max(...subjectEntries.map(e => e.score));
    return { count, avg, best };
  };

  // Basculer l'extension d'une section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Basculer l'extension d'une matière
  const toggleSubject = (subject) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subject]: !prev[subject]
    }));
  };

  // Initialize edit values when entering edit mode
  const startEditing = () => {
    setEditValue({
      lastName: currentUser?.lastName || '',
      firstName: currentUser?.firstName || '',
      pseudonym: currentUser?.pseudonym || '',
      email: currentUser?.email || ''
    });
    setIsEditing(true);
    setShowEmailEdit(false);
    setCurrentPasswordForEmail('');
    setMessage('');
  };

  // Sauvegarder les modifications
  const saveProfile = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setMessage('');

      const emailChanged =
        editValue.email.trim().toLowerCase() !==
        String(currentUser.email || '').trim().toLowerCase();
      const result = await updateProfile({
        ...editValue,
        currentPassword: emailChanged ? currentPasswordForEmail : undefined,
      });
      setCurrentPasswordForEmail('');
      setIsEditing(false);
      setShowEmailEdit(false);
      setMessage(
        result?.requiresRelogin
          ? 'Adresse e-mail modifiée. Reconnectez-vous avec votre nouvelle adresse.'
          : 'Profil mis à jour avec succès !'
      );
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      setMessage('Erreur lors de la mise à jour du profil');
    }

    setLoading(false);
  };

  // Annuler l'édition
  const cancelEdit = () => {
    setEditValue({
      lastName: currentUser?.lastName || '',
      firstName: currentUser?.firstName || '',
      pseudonym: currentUser?.pseudonym || '',
      email: currentUser?.email || ''
    });
    setIsEditing(false);
    setShowEmailEdit(false);
    setMessage('');
  };

  // Gérer le changement de mot de passe
  const handleResetPassword = async () => {
    if (!currentUser?.email) return;

    try {
      setLoading(true);
      setMessage('');
      await resetPassword(currentUser.email);
      setMessage('Email de réinitialisation du mot de passe envoyé !');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      setMessage('Erreur lors de l\'envoi de l\'email de réinitialisation');
    }
    setLoading(false);
  };

  // Données affichées
  const displayName = currentUser?.pseudonym || 'Anonyme';
  const displayEmail = currentUser?.email || 'Non connecté';
  const createdAt = currentUser?.createdAt 
    ? new Date(currentUser.createdAt).toLocaleDateString('fr-FR')
    : 'N/A';

  // Couleurs des badges
  const getBadgeColor = (color) => {
    const colors = {
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    };
    return colors[color] || colors.blue;
  };

  // Icônes des matières
  const getSubjectIcon = (subject) => {
    const icons = {
      'Mathématiques': '📐',
      'Français': '✍️',
      'Culture Générale': '🌍',
      'Droit & Administration': '⚖️',
      'Simulation': '📝',
      'default': '📚'
    };
    return icons[subject] || icons.default;
  };

  const fullName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean).join(' ').trim() || displayName;
  const achievedLevels = BADGE_LEVELS.filter((level) => quizStats.totalQuizzes >= level.count);
  const currentBadge = achievedLevels[achievedLevels.length - 1] || null;
  const nextBadge = BADGE_LEVELS.find((level) => quizStats.totalQuizzes < level.count) || null;
  const specialBadges = quizStats.badges.filter(
    (badge) => !BADGE_LEVELS.some((level) => badge.name === `${level.icon} ${level.label}`)
  );
  const premiumActive = Boolean(accessProfile?.premiumActive);
  const expiration = accessProfile?.premiumExpiresAt
    ? new Date(accessProfile.premiumExpiresAt)
    : null;
  const expirationLabel = expiration && !Number.isNaN(expiration.getTime())
    ? expiration.toLocaleDateString('fr-FR', { dateStyle: 'long' })
    : null;

  return (
    <div className="cb-profile space-y-6">
      <section className="cb-profile-hero" aria-labelledby="profile-title">
        <div className="cb-profile-identity">
          <div className="cb-profile-avatar" aria-hidden="true">
            {currentUser ? fullName.slice(0, 1).toLocaleUpperCase('fr-FR') : '·'}
          </div>
          <div className="cb-profile-identity-copy">
            <h1 id="profile-title">{currentUser ? fullName : 'Mon profil'}</h1>
            <p>{currentUser?.email || 'Connectez-vous pour accéder à votre espace personnel'}</p>
            {currentUser?.pseudonym && <small>@{currentUser.pseudonym}</small>}
          </div>
        </div>
        <div className="cb-profile-membership">
          <span className={premiumActive ? 'cb-profile-membership-label is-premium' : 'cb-profile-membership-label'}>
            {premiumActive ? '✦ Premium actif' : 'Accès Free'}
          </span>
          {premiumActive && expirationLabel && <p>Valable jusqu’au {expirationLabel}</p>}
          {currentUser && <button type="button" className="cb-profile-primary-action"
            onClick={jumpToSubscription}>
            {premiumActive ? 'Consulter mon abonnement' : 'Passer à Premium'}
            <span aria-hidden="true">↓</span>
          </button>}
        </div>
      </section>

      {message && (
        <div role="status" className={`cb-profile-feedback ${message.includes('Erreur') ? 'is-error' : 'is-success'}`}>
          {message}
        </div>
      )}

      <nav className="cb-profile-shortcuts" role="tablist" aria-label="Rubriques de mon espace">
        {[
          ['account', 'Mon compte'],
          ['subscription', 'Mon abonnement'],
          ['progress', 'Progression'],
          ['referral', 'Parrainage'],
        ].map(([key, label]) => (
          <button key={key} type="button" role="tab"
            id={`cb-profile-tab-${key}`}
            aria-controls={key === 'subscription' ? 'profile-subscription' : `cb-profile-${key}-panel`}
            aria-selected={activeSection === key}
            tabIndex={activeSection === key ? 0 : -1}
            className={activeSection === key ? 'is-active' : ''}
            onClick={() => setActiveSection(key)}
            onKeyDown={(event) => {
              const names = ['account', 'subscription', 'progress', 'referral'];
              const current = names.indexOf(key);
              let next = current;
              if (event.key === 'ArrowRight') next = (current + 1) % names.length;
              else if (event.key === 'ArrowLeft') next = (current + names.length - 1) % names.length;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = names.length - 1;
              else return;
              event.preventDefault();
              setActiveSection(names[next]);
              event.currentTarget.parentElement?.querySelectorAll('[role="tab"]')[next]?.focus();
            }}>
            {label}
          </button>
        ))}
      </nav>

      {activeSection === 'subscription' && (
        <section id="profile-subscription" role="tabpanel" tabIndex={-1}
          aria-labelledby="cb-profile-tab-subscription"
          ref={subscriptionRef} className="cb-profile-subscription">
          <BillingPanel embedded onBackToContent={onBackToContent} />
        </section>
      )}

      {activeSection === 'referral' && (
        <section id="cb-profile-referral-panel" role="tabpanel"
          aria-labelledby="cb-profile-tab-referral">
          <ReferralPanel />
        </section>
      )}

      {activeSection === 'account' && (
        <section id="cb-profile-account-panel" role="tabpanel"
          aria-labelledby="cb-profile-tab-account" className="cb-profile-panel">
          <div className="cb-profile-section-heading">
            <div>
              <h2>Informations et sécurité</h2>
            </div>
            <p>Gérez vos informations personnelles et vos paramètres de compte.</p>
          </div>
          <div className="cb-profile-grid cb-profile-account-grid grid gap-5">

        {/* INFORMATIONS PERSONNELLES - Section dépliable */}
        <div id="cb-profile-info" className="surface-card rounded-2xl shadow-sm border border-color transition-all overflow-hidden cb-profile-detail-card">
          {/* En-tête de la section (toujours visible) */}
          <button type="button"
            aria-expanded={expandedSections.personalInfo}
            aria-controls="cb-profile-personal-content"
            onClick={() => toggleSection('personalInfo')}
            className="w-full px-6 py-5 flex items-center justify-between hover:bg-opacity-80 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center text-xl">👤</div>
              <div className="text-left">
                <h2 className="text-xl font-bold">Informations Personnelles</h2>
                {!expandedSections.personalInfo && currentUser && (
                  <p className="text-sm text-secondary">
                    {currentUser.firstName || currentUser.lastName ? 
                      `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : 
                      displayName}
                  </p>
                )}
              </div>
            </div>
            <span className="cb-profile-accordion-status" aria-hidden="true">
              {expandedSections.personalInfo ? 'Masquer' : 'Afficher'}
            </span>
          </button>
          
          {/* Contenu dépliable */}
          {expandedSections.personalInfo && (
            <div id="cb-profile-personal-content" className="px-6 pb-6 space-y-4 border-t border-color">
              {!currentUser ? (
                <p className="text-secondary italic py-4">
                  Veuillez vous connecter pour accéder à votre profil
                </p>
              ) : isEditing ? (
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="block text-sm text-secondary mb-1">Nom</label>
                    <input
                      type="text"
                      value={editValue.lastName}
                      onChange={(e) => setEditValue({ ...editValue, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-secondary mb-1">Prénom</label>
                    <input
                      type="text"
                      value={editValue.firstName}
                      onChange={(e) => setEditValue({ ...editValue, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-secondary mb-1">Pseudonyme</label>
                    <input
                      type="text"
                      value={editValue.pseudonym}
                      onChange={(e) => setEditValue({ ...editValue, pseudonym: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                      placeholder="Votre pseudonyme"
                      maxLength={20}
                    />
                  </div>
                  
                  {!showEmailEdit ? (
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="block text-sm text-secondary mb-1">Email</span>
                        <span className="font-semibold">{editValue.email}</span>
                      </div>
                      <button
                        onClick={() => setShowEmailEdit(true)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Modifier
                      </button>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm text-secondary mb-1">Nouvel Email</label>
                      <input
                        type="email"
                        value={editValue.email}
                        onChange={(e) => setEditValue({ ...editValue, email: e.target.value })}
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                        placeholder="Votre nouvel email"
                      />
                      <label className="block text-sm text-secondary mt-3 mb-1">
                        Mot de passe actuel
                      </label>
                      <input
                        type="password"
                        value={currentPasswordForEmail}
                        onChange={(e) => setCurrentPasswordForEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600 text-primary"
                        placeholder="Confirmez votre mot de passe"
                        autoComplete="current-password"
                      />
                      <button
                        onClick={() => {
                          setShowEmailEdit(false);
                          setCurrentPasswordForEmail('');
                          setEditValue((previous) => ({
                            ...previous,
                            email: currentUser?.email || '',
                          }));
                        }}
                        className="text-red-600 hover:text-red-800 text-sm mt-1"
                      >
                        Annuler
                      </button>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={saveProfile}
                      disabled={loading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Sauvegarde...' : '✓ Sauvegarder'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      ✕ Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 pt-4">
                  <p className="flex justify-between">
                    <span className="text-secondary">Nom</span>
                    <span className="font-semibold">{currentUser.lastName || 'Non défini'}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-secondary">Prénom</span>
                    <span className="font-semibold">{currentUser.firstName || 'Non défini'}</span>
                  </p>
                  <p className="flex justify-between items-center">
                    <span className="text-secondary">Pseudonyme</span>
                    <span className="font-semibold">{displayName}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-secondary">Email</span>
                    <span className="font-semibold">{displayEmail}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-secondary">Membre depuis</span>
                    <span className="font-semibold">{createdAt}</span>
                  </p>
                  
                  <button 
                    onClick={startEditing}
                    className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors"
                  >
                    Modifier le Profil
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* SÉCURITÉ - Section dépliable */}
        <div className="surface-card rounded-2xl shadow-sm border border-color transition-all overflow-hidden cb-profile-detail-card">
          <button type="button"
            aria-expanded={expandedSections.security}
            aria-controls="cb-profile-security-content"
            onClick={() => toggleSection('security')}
            className="w-full px-6 py-5 flex items-center justify-between hover:bg-opacity-80 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-xl">🔒</div>
              <div className="text-left">
                <h2 className="text-xl font-bold">Sécurité</h2>
                {!expandedSections.security && (
                  <p className="text-sm text-secondary">Mot de passe</p>
                )}
              </div>
            </div>
            <span className="cb-profile-accordion-status" aria-hidden="true">
              {expandedSections.security ? 'Masquer' : 'Afficher'}
            </span>
          </button>
          
          {expandedSections.security && (
            <div id="cb-profile-security-content" className="px-6 pb-6 space-y-4 border-t border-color">
              {!currentUser ? (
                <p className="text-secondary italic py-4">
                  Veuillez vous connecter pour accéder aux paramètres de sécurité
                </p>
              ) : (
                <div className="space-y-4 pt-4">
                  <div className="bg-secondary p-4 rounded-xl">
                    <h3 className="font-semibold mb-2">Mot de passe</h3>
                    <p className="text-sm text-secondary mb-4">
                      Recevez un email pour réinitialiser votre mot de passe
                    </p>
                    <button
                      onClick={handleResetPassword}
                      disabled={loading}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Envoi...' : '📧 Réinitialiser le mot de passe'}
                    </button>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl text-sm text-secondary">
                    <p>📌 Un email de réinitialisation sera envoyé à votre adresse email.</p>
                    <p>Vérifiez votre boîte de réception et suivez les instructions.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* PRÉFÉRENCES ET DROITS DU COMPTE. Le formulaire reste monté :
            les saisies en cours sont conservées si la carte est repliée. */}
        <div className="cb-profile-settings-wide surface-card rounded-2xl shadow-sm border border-color transition-all overflow-hidden cb-profile-detail-card">
          <button type="button"
            aria-expanded={expandedSections.preferences}
            aria-controls="cb-profile-preferences-content"
            onClick={() => toggleSection('preferences')}
            className="w-full px-6 py-5 flex items-center justify-between hover:bg-opacity-80 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-xl" aria-hidden="true">⚙️</div>
              <div className="text-left">
                <h2 className="text-xl font-bold">Préférences &amp; compte</h2>
                {!expandedSections.preferences && (
                  <p className="text-sm text-secondary">Préparation et paramètres du compte</p>
                )}
              </div>
            </div>
            <span className="cb-profile-accordion-status" aria-hidden="true">
              {expandedSections.preferences ? 'Masquer' : 'Afficher'}
            </span>
          </button>
          <div id="cb-profile-preferences-content"
            className="cb-profile-preferences-content border-t border-color"
            hidden={!expandedSections.preferences}>
            <AccountSettings embedded />
          </div>
        </div>
          </div>
        </section>
      )}

      {activeSection === 'progress' && (
        <section id="cb-profile-progress-panel" role="tabpanel"
          aria-labelledby="cb-profile-tab-progress" className="cb-profile-panel">
          <div className="cb-profile-section-heading">
            <div><h2>Ma progression</h2></div>
            <p>Retrouvez vos scores, vos badges et votre historique d’évaluations.</p>
          </div>
          <div className="cb-profile-grid grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* PROGRESSION (Quizzes + Simulations) */}
        <div id="cb-profile-progress" className="surface-card p-6 rounded-2xl shadow-sm border border-color transition-all cb-profile-detail-card">
          <h2 className="text-xl font-bold mb-6">📊 Progression</h2>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-semibold text-secondary">Globale</span>
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{calculateProgress()}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-3">
                <div 
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-secondary p-3 rounded-xl text-center">
                <p className="text-xs text-secondary uppercase">Évaluations</p>
                <p className="text-lg font-bold">{quizStats.totalQuizzes}</p>
              </div>
              <div className="bg-secondary p-3 rounded-xl text-center">
                <p className="text-xs text-secondary uppercase">Moyenne</p>
                <p className="text-lg font-bold">{quizStats.averageScore}/20</p>
              </div>
            </div>
            
            {quizStats.totalQuizzes === 0 && (
              <p className="text-sm text-secondary text-center py-4">
                Commencez des quizzes ou simulations pour voir votre progression !
              </p>
            )}
          </div>
        </div>

        {/* BADGES : un niveau principal lisible + jalons ordonnés.
            Les distinctions particulières sont conservées séparément. */}
        <div className="surface-card rounded-2xl shadow-sm border border-color transition-all cb-profile-detail-card cb-profile-badges">
          <h2 className="cb-profile-badges-title">🏆 Mes badges</h2>
          <div className="cb-profile-badges-top">
            <div className="cb-profile-current-badge">
              <span className="cb-profile-badges-caption">Mon niveau actuel</span>
              <strong>{currentBadge ? `${currentBadge.icon} ${currentBadge.label}` : 'Pas encore de badge'}</strong>
              <span className="cb-profile-badges-caption">
                {nextBadge
                  ? `Prochain : ${nextBadge.label} dans ${nextBadge.count - quizStats.totalQuizzes} évaluation${nextBadge.count - quizStats.totalQuizzes > 1 ? 's' : ''}`
                  : 'Tous les niveaux de parcours sont atteints'}
              </span>
            </div>
          </div>
          <ol className="cb-profile-badge-levels" aria-label="Niveaux de parcours par nombre d’évaluations">
            {BADGE_LEVELS.map((level) => {
              const unlocked = quizStats.totalQuizzes >= level.count;
              const active = currentBadge?.count === level.count;
              return (
                <li key={level.count} className={`cb-profile-badge-level ${unlocked ? 'is-earned' : 'is-locked'} ${active ? 'is-current' : ''}`}
                  aria-label={`${level.label}, ${level.count} évaluation${level.count > 1 ? 's' : ''}, ${unlocked ? 'obtenu' : 'à obtenir'}`}>
                  <span aria-hidden="true">{level.icon}</span>
                  <span className="cb-profile-badge-level-label">{level.label}</span>
                  <small>{level.count} éval.</small>
                </li>
              );
            })}
          </ol>
          {specialBadges.length > 0 && (
            <details className="cb-profile-special-badges">
              <summary>Autres distinctions obtenues ({specialBadges.length})</summary>
              <div className="cb-profile-special-badges-list">
                {specialBadges.map((badge) => (
                  <span key={badge.name}
                    className={`cb-profile-special-badge ${getBadgeColor(badge.color)}`}
                    title={badge.desc}>{badge.name}</span>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* HISTORIQUE GROUPÉ PAR MATIÈRE (Quizzes + Simulations) */}
        <div className="surface-card p-6 rounded-2xl shadow-sm border border-color transition-all md:col-span-2 cb-profile-detail-card">
          <div className="cb-profile-history-heading">
            <div>
              <h2>Historique des évaluations</h2>
              <p>{quizStats.totalQuizzes} évaluation{quizStats.totalQuizzes !== 1 ? 's' : ''} enregistrée{quizStats.totalQuizzes !== 1 ? 's' : ''}</p>
            </div>
            <button type="button"
              aria-expanded={showProgressHistory}
              aria-controls="cb-profile-training-history"
              onClick={() => setShowProgressHistory((shown) => !shown)}>
              {showProgressHistory ? 'Masquer l’historique' : 'Voir l’historique'}
            </button>
          </div>
          {showProgressHistory && (
            <div id="cb-profile-training-history">
              {Object.keys(historyBySubject).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(historyBySubject).map(([subject, entries]) => {
                const stats = getSubjectStats(entries);
                const isExpanded = expandedSubjects[subject];
                
                return (
                  <div key={subject} className="border border-color rounded-xl overflow-hidden">
                    {/* En-tête de la matière (cliquable) */}
                    <button
                      onClick={() => toggleSubject(subject)}
                      className="w-full px-4 py-4 bg-secondary hover:bg-opacity-80 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{getSubjectIcon(subject)}</span>
                        <div className="text-left">
                          <p className="font-bold text-lg">{subject}</p>
                          <p className="text-sm text-secondary">
                            {stats.count} {stats.count > 1 ? 'évaluations' : 'évaluation'} • Moy: {stats.avg}/20 • Best: {stats.best}/20
                          </p>
                        </div>
                      </div>
                    </button>
                    
                    {/* Détails de la matière (dépliables) */}
                    {isExpanded && (
                      <div className="divide-y divide-color">
                        {entries.map((entry, index) => (
                          <div key={index} className="px-4 py-3 flex justify-between items-center hover:bg-white dark:hover:bg-slate-700">
                            <div>
                              <p className="font-medium">{entry.sujet || entry.titre}</p>
                              <p className="text-xs text-secondary">
                                {new Date(entry.date).toLocaleDateString('fr-FR')} • {entry.lot}
                                {entry.est_sujets_officiels && <span className="ml-2 text-purple-600">🏆 Officiel</span>}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`font-bold ${
                                entry.score >= 14 ? 'text-green-500' : 
                                entry.score >= 10 ? 'text-yellow-500' : 'text-red-500'
                              }`}>
                                {entry.score}/20
                              </span>
                              <p className="text-xs text-secondary">
                                {entry.examMode ? '⏱️ Examen' : '📚 Entraînement'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-secondary text-center py-4">
              Aucune évaluation complétée pour le moment. Lancez-vous !
            </p>
              )}
            </div>
          )}
        </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Profile;
